import React, { useMemo, useRef, useState } from "react";
import { TextField, InputAdornment, IconButton, makeStyles } from "@material-ui/core";
import { LuClock } from "react-icons/lu";
import { RiEyeLine, RiEyeOffLine, RiCalendarLine, RiQuestionFill, RiSearchLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        position: "relative",
        "& input[type='date']::-webkit-calendar-picker-indicator, & input[type='month']::-webkit-calendar-picker-indicator, & input[type='time']::-webkit-calendar-picker-indicator": {
            display: "none",
            WebkitAppearance: "none",
        },
        "& input[type='date']::-webkit-inner-spin-button, & input[type='month']::-webkit-inner-spin-button, & input[type='time']::-webkit-inner-spin-button": {
            display: "none",
            WebkitAppearance: "none",
        },
        "& input[type='date']::-webkit-clear-button, & input[type='month']::-webkit-clear-button, & input[type='time']::-webkit-clear-button": {
            display: "none",
            WebkitAppearance: "none",
        },
        "& input[type='date'], & input[type='month'], & input[type='time']": {
            appearance: "none",
            MozAppearance: "textfield",
        },
    },
    firefoxInput: {
        clipPath: "inset(0 24px 0 0)",
        marginRight: "-24px !important",
    },
    labelRoot: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    colorPreview: {
        width: "24px",
        height: "24px",
        borderRadius: "2px",
        border: "1px solid #ccc",
        marginRight: "8px",
        cursor: "pointer",
        backgroundColor: (props) => props.colorValue || "#000000"
    },
    tooltipIcon: {
        display: 'inline-flex',
        verticalAlign: 'middle',
        cursor: 'default',
        marginLeft: 4
    },
    hiddenColorInput: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        opacity: 0,
        border: 0,
        padding: 0,
        margin: 0,
        pointerEvents: 'none'
    },
}));

export default function CustomInput({
    label,
    tipoInputRight = null,
    tipoInputLeft = null,
    width = "auto",
    containerStyle = "",
    required = false,
    ...props
}) {
    const classes = useStyles({ colorValue: props.value });
    const [showPassword, setShowPassword] = useState(false);

    const isFirefox = useMemo(() =>
        typeof window !== "undefined" && navigator.userAgent.toLowerCase().includes("firefox"),
        []
    );

    const isMobile = useMemo(() =>
        window.matchMedia("(any-pointer: coarse)").matches || window.matchMedia("only screen and (max-width: 760px)").matches,
        []
    );

    const hasError = Boolean(props?.touched?.[props.name] && props?.errors?.[props.name]);
    const helperText = hasError ? props?.errors?.[props.name] : null;

    const inputRef = useRef(null);
    const refColorInput = useRef(null);

    const openCalendar = (e) => {
        if (props.disabled) return;
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        try {
            if (props.type === 'color' && refColorInput.current) {
                refColorInput.current.click();
            } else if (inputRef.current) {
                if ('showPicker' in inputRef.current) {
                    inputRef.current.showPicker();
                } else {
                    inputRef.current.focus();
                }
            }
        } catch (error) {
            console.error("Erro ao abrir picker:", error);
        }
    };

    const getInputType = () => {
        if (tipoInputRight === "password") return showPassword ? "text" : "password";
        if (props.type === "color") return "text";
        if (isFirefox && !isMobile && props.type === "month") return "date";
        return props.type || "text";
    };

    const renderLabel = () => {
        if (!label) return null;

        return (
            <span className={classes.labelRoot}>
                {label}
                {required && <span> *</span>}
            </span>
        );
    };

    const handleKeyDown = (e) => {
        if (props.type != "number") return;

        const invalidChars = ["e", "E", "+", "-"];

        if (invalidChars.includes(e.key)) {
            e.preventDefault();
        }
    };

    const startAdornment = () => {
        if (tipoInputLeft === "search") {
            return (
                <InputAdornment position="start">
                    <RiSearchLine size={20} color="#777777" />
                </InputAdornment>
            );
        }
        if (props.type === "color") {
            return (
                <InputAdornment position="start">
                    <div
                        className={classes.colorPreview}
                        onClick={openCalendar}
                    />
                </InputAdornment>
            );
        }
        return null;
    };

    const endAdornment = () => {
        if (props.type === "time") {
            return (
                <InputAdornment position="end">
                    <IconButton onClick={openCalendar} edge="end">
                        <LuClock size={20} color="#777777" />
                    </IconButton>
                </InputAdornment>
            );
        }

        if (tipoInputRight === "password") {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                    >
                        {!showPassword ? <RiEyeOffLine size={20} /> : <RiEyeLine size={20} color="#0D9F0A" />}
                    </IconButton>
                </InputAdornment>
            );
        }

        if (["data_cartao", "mes"].includes(tipoInputRight)) {
            return (
                <InputAdornment position="end">
                    <IconButton onClick={openCalendar} edge="end" style={tipoInputRight == "data_cartao" ? { pointerEvents: 'none' } : null}>
                        <RiCalendarLine size={20} color="#777777" />
                    </IconButton>
                </InputAdornment>
            );
        }

        if (tipoInputRight === "cvv") {
            return (
                <InputAdornment position="end">
                    <OverlayTrigger placement='top' trigger={['hover']}
                        overlay={
                            <Tooltip className="mt-2 mb-2" >
                                <p style={{ margin: 0 }} >O código de segurança é um número de 3 ou 4 dígitos impresso no verso do seu cartão.</p>
                            </Tooltip>
                        } >
                        <span tabIndex="0" style={{ cursor: 'pointer', display: 'flex' }}>
                            <RiQuestionFill size={20} color="#777777" />
                        </span>
                    </OverlayTrigger>

                </InputAdornment>
            );
        }

        if (tipoInputRight === "tipo_despesa") {
            return (
                <InputAdornment position="end">
                    <OverlayTrigger placement='top' trigger={['hover']}
                        overlay={
                            <Tooltip className="mt-2 mb-2" >
                                <p style={{ margin: 0 }} >Ao definir um limite de despesas para a tag, você receberá um alerta assim que o valor total associado a ela for atingido.</p>
                            </Tooltip>
                        } >
                        <span tabIndex="0" style={{ cursor: 'pointer', display: 'flex' }}>
                            <RiQuestionFill size={20} color="#777777" />
                        </span>
                    </OverlayTrigger>
                </InputAdornment>
            );
        }

        if (tipoInputRight === "limite_cartao") {
            return (
                <InputAdornment position="end">
                    <OverlayTrigger placement='bottom' trigger={['hover']}
                        overlay={
                            <Tooltip className="mt-2 mb-2" >
                                <p style={{ margin: 0 }} >Ao vincular o cartão a uma conta bancária, todas as despesas serão associadas a ela, facilitando a previsão de saldo. Caso não haja vínculo definido, o saldo será subtraído do saldo total do mês.</p>
                            </Tooltip>
                        } >
                        <span tabIndex="0" style={{ cursor: 'pointer', display: 'flex' }}>
                            <RiQuestionFill size={20} color="#777777" />
                        </span>
                    </OverlayTrigger>
                </InputAdornment>
            );
        }

        return null;
    };

    return (
        <div className={`${classes.root} ${containerStyle}`} style={{ width }}>
            {/* Input Oculto para seletor nativo de cor */}
            {props.type === "color" && (
                <input
                    type="color"
                    ref={refColorInput}
                    className={classes.hiddenColorInput}
                    onChange={props.onChange}
                    name={props.name}
                    value={props.value}
                    tabIndex={-1}
                />
            )}

            <TextField
                variant="outlined"
                fullWidth
                label={renderLabel()}
                name={props.name}
                value={props.value}
                onChange={props.type === "color" ? undefined : props.onChange}
                onBlur={props.type === "color" ? undefined : props.onBlur}
                onClick={props.type === "color" || props.type === "time" || tipoInputRight === "mes" ? openCalendar : undefined}
                onKeyDown={handleKeyDown}
                disabled={props.disabled}
                placeholder={props.placeholder}
                type={getInputType()}
                error={hasError}
                helperText={helperText}
                inputRef={inputRef}
                InputLabelProps={{
                    shrink: props.value || ["date", "month", "color", "datetime-local", "time"].includes(props.type) ? true : undefined,
                    ...props.InputLabelProps
                }}
                InputProps={{
                    startAdornment: startAdornment(),
                    endAdornment: endAdornment(),
                    readOnly: props.type === "color",
                    className: props.classNameInput || "",
                    ...props.InputProps
                }}
                inputProps={{
                    maxLength: props.maxLength || 255,
                    min: props.min,
                    max: props.max,
                    inputMode: props.inputMode,
                    className: (isFirefox && ["date", "month", "time"].includes(props.type)) ? classes.firefoxInput : "",
                    ...props.inputProps
                }}
            />
        </div>
    );
}